import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input, Drawer, Spin, Select, Modal, DatePicker, message, Tooltip } from 'antd';
import { ExportOutlined, RedoOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { ButtonWrapper } from 'components';
import { FilterAtnButton } from 'utils/commonStyled';
import { AntdTable } from 'components/custom-table/Style';
import { Box, Flex, Text } from 'atoms';
import { FilterTitle, FilterTag } from 'components/common';
import {
	getTableColumns,
	StatusOptions,
	VendorTypeOptions,
	LegalStructureOptions,
} from './VendorListPageV2.config';
import { searchVendorV2, updateVendorV2, exportVendorCSVV2 } from 'store/vendorSliceV2';
import { searchBusinessUnitsV2 } from 'store/projectSliceV2';
import { usePersistedState } from 'hooks';

const { Search } = Input;
const { RangePicker } = DatePicker;

const LtSearchBar = styled(Search)`
	border: none;
`;

const StyledFilterButton = styled(FilterAtnButton)`
	margin-left: 16px;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

export default function VendorListPageV2() {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();

	const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
	const [searchValue, setSearchValue] = usePersistedState('vendorList:search', ''); // Input value
	const [searchTerm, setSearchTerm] = usePersistedState('vendorList:searchTerm', ''); // Actual search term used for filtering
	const [loading, setLoading] = useState(false);
	const [vendorsData, setVendorsData] = useState([]);
	const [pagination, setPagination] = usePersistedState('vendorList:pagination', {
		current: 1,
		pageSize: 10,
		total: 0,
	});
	const [filters, setFilters] = usePersistedState('vendorList:filters', {});
	const [dateRange, setDateRange] = useState([null, null]);
	const [sortBy, setSortBy] = usePersistedState('vendorList:sortBy', null);
	const [statusModalVisible, setStatusModalVisible] = useState(false);
	const [selectedVendor, setSelectedVendor] = useState(null);
	const [newStatus, setNewStatus] = useState(null);
	const [updatingStatus, setUpdatingStatus] = useState(false);
	const [exporting, setExporting] = useState(false);

	// Filter Options State
	const [filterOptions, setFilterOptions] = useState({
		cities: [],
		businessUnits: [],
	});

	// Fetch vendors on mount and when pagination/filters/sort/search change
	useEffect(() => {
		fetchVendors();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.current, pagination.pageSize, filters, sortBy, searchTerm]);

	// Refresh vendors when returning from edit page
	useEffect(() => {
		if (location.state?.refresh) {
			fetchVendors();
			// Clear the refresh flag
			navigate(location.pathname, { replace: true, state: {} });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state]);

	useEffect(() => {
		if (filterDrawerOpen && filterOptions.businessUnits.length === 0) {
			fetchBusinessUnits();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterDrawerOpen]);

	/**
	 * Build filter_by object for API
	 * Maps search term to query_str key for general search across vendor fields
	 */
	const buildFilterBy = () => ({
		// Map search term to query_str for general search across vendor fields
		...(searchTerm && { query_str: searchTerm }),
		...(filters?.vendor_type && { vendor_type: filters.vendor_type }),
		...(filters?.legal_structure && { legal_structure: filters.legal_structure }),
		...(filters?.business_unit_id && { business_unit_id: filters.business_unit_id }),
		...(filters?.city && { city: filters.city }),
		...(filters?.rating_min && { rating_min: filters.rating_min }),
		...(filters?.rating_max && { rating_max: filters.rating_max }),
		...(filters?.is_active !== undefined && { is_active: filters.is_active }),
		...(filters?.created_from && { created_from: filters.created_from }),
		...(filters?.created_to && { created_to: filters.created_to }),
	});

	const extractFilters = (rows) => {
		setFilterOptions((prev) => {
			const cities = [...(prev.cities || [])];

			rows.forEach((v) => {
				const city = v.vendor_info?.address?.city;
				if (city && !cities.some((c) => c.value === city)) {
					cities.push({ label: city, value: city });
				}
			});

			return { ...prev, cities };
		});
	};

	const fetchBusinessUnits = async () => {
		try {
			const response = await dispatch(searchBusinessUnitsV2({ limit: 100 }));
			setFilterOptions((prev) => ({
				...prev,
				businessUnits: (response?.results || []).sort((a, b) =>
					(a?.label || '').localeCompare(b?.label || '')
				),
			}));
		} catch (error) {
			console.error('Error fetching business units:', error);
			message.error('Failed to fetch business units');
		}
	};

	/**
	 * Handle successful API response
	 */
	const handleVendorResponse = (response) => {
		if (response && typeof response === 'object') {
			const rows = Array.isArray(response.rows) ? response.rows : [];
			setVendorsData(rows);
			setPagination((prev) => ({
				...prev,
				total: response.count || 0,
			}));
			extractFilters(rows);
		} else {
			setVendorsData([]);
			setPagination((prev) => ({
				...prev,
				total: 0,
			}));
		}
	};

	/**
	 * Handle error state
	 */
	const handleVendorError = (error) => {
		console.error('Error fetching vendors:', error);
		setVendorsData([]);
		setPagination((prev) => ({
			...prev,
			total: 0,
		}));
		message.error('Failed to fetch vendors. Please try again.');
	};

	const fetchVendors = async () => {
		setLoading(true);
		try {
			const filterBy = buildFilterBy();
			const response = await dispatch(
				searchVendorV2({
					filter_by: filterBy || {},
					pagination: {
						page: pagination?.current || 1,
						per_page: pagination?.pageSize || 10,
					},
					sort_by: sortBy || null,
				})
			);

			handleVendorResponse(response);
		} catch (error) {
			handleVendorError(error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (value) => {
		setSearchValue(value);
		setSearchTerm(value); // Update the actual search term used for filtering
		setPagination((prev) => ({
			...prev,
			current: 1, // Reset to first page on search
		}));
	};

	const handleTableChange = (paginationConfig, tableFilters, sorter) => {
		setPagination({
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
			total: paginationConfig.total,
		});

		// Handle sorting
		if (sorter && sorter.field) {
			const sortableFields = [
				'vendor_name',
				'created_at',
				'updated_at',
				'rating',
				'compliance_status',
			];
			if (sortableFields.includes(sorter.field)) {
				setSortBy({
					field: sorter.field,
					order: sorter.order === 'ascend' ? 'asc' : 'desc',
				});
			} else {
				setSortBy(null);
			}
		} else {
			setSortBy(null);
		}
	};

	const handleExport = async () => {
		setExporting(true);
		try {
			const filterBy = buildFilterBy();
			const response = await dispatch(
				exportVendorCSVV2({
					filter_by: filterBy || {},
					format: 'csv',
				})
			);

			if (!response || response.status !== 'success') {
				const errorData = response?.error || response || {};
				const errorMessage =
					errorData?.message || errorData?.details?.message || 'Failed to export vendors';
				message.error(errorMessage);
			}
		} catch (error) {
			console.error('Error exporting vendors:', error);
			const errorMessage = error?.message || 'Failed to export vendors. Please try again.';
			message.error(errorMessage);
		} finally {
			setExporting(false);
		}
	};

	const handleSync = () => {
		fetchVendors();
	};

	const handleCreate = () => {
		navigate('/vendors/add');
	};

	const handleFilterChange = (filterKey, value) => {
		setFilters((prev) => ({
			...prev,
			[filterKey]: value,
		}));
		setPagination((prev) => ({
			...prev,
			current: 1, // Reset to first page when filter changes
		}));
	};

	const handleDateRangeChange = (dates) => {
		setDateRange(dates || [null, null]);
		if (dates && dates[0] && dates[1]) {
			setFilters((prev) => ({
				...prev,
				created_from: moment(dates[0]).startOf('day').toISOString(),
				created_to: moment(dates[1]).endOf('day').toISOString(),
			}));
		} else {
			setFilters((prev) => {
				const { created_from, created_to, ...rest } = prev;
				return rest;
			});
		}
		setPagination((prev) => ({
			...prev,
			current: 1,
		}));
	};

	const handleResetFilters = () => {
		setFilters({});
		setDateRange([null, null]);
		setSearchValue('');
		setSearchTerm(''); // Reset search term
		setSortBy(null);
		setPagination((prev) => ({
			...prev,
			current: 1,
		}));
		setFilterDrawerOpen(false);
	};

	// Helper functions to check if filters are applied
	const hasDateRangeFilter = () => dateRange && dateRange[0] && dateRange[1];
	const hasStatusFilter = () => filters.is_active !== undefined && filters.is_active !== null;
	const hasVendorTypeFilter = () =>
		filters.vendor_type !== undefined && filters.vendor_type !== null;
	const hasLegalStructureFilter = () =>
		filters.legal_structure !== undefined && filters.legal_structure !== null;
	const hasBusinessUnitFilter = () =>
		filters.business_unit_id !== undefined && filters.business_unit_id !== null;
	const hasCityFilter = () => filters.city !== undefined && filters.city !== null;

	const activeFiltersCount = [
		hasDateRangeFilter(),
		hasStatusFilter(),
		hasVendorTypeFilter(),
		hasLegalStructureFilter(),
		hasBusinessUnitFilter(),
		hasCityFilter(),
	].filter(Boolean).length;

	// Handlers to remove individual filters
	const handleRemoveDateRangeFilter = () => handleDateRangeChange([null, null]);
	const handleRemoveStatusFilter = () => handleFilterChange('is_active', undefined);
	const handleRemoveVendorTypeFilter = () => handleFilterChange('vendor_type', undefined);
	const handleRemoveLegalStructureFilter = () => handleFilterChange('legal_structure', undefined);
	const handleRemoveBusinessUnitFilter = () => handleFilterChange('business_unit_id', undefined);
	const handleRemoveCityFilter = () => handleFilterChange('city', undefined);

	const handleStatusToggle = (vendor, newStatusValue) => {
		if (!vendor || typeof vendor !== 'object') {
			message.error('Invalid vendor data');
			return;
		}

		if (!vendor.id) {
			message.error('Vendor ID is missing');
			return;
		}

		setSelectedVendor(vendor);
		setNewStatus(newStatusValue);
		setStatusModalVisible(true);
	};

	const handleStatusUpdateConfirm = async () => {
		if (!selectedVendor || typeof selectedVendor !== 'object') {
			message.error('Invalid vendor data');
			return;
		}

		if (!selectedVendor.id) {
			message.error('Vendor ID is missing');
			setStatusModalVisible(false);
			return;
		}

		setUpdatingStatus(true);
		try {
			const response = await dispatch(
				updateVendorV2({
					req_data: {
						id: selectedVendor.id,
						is_active:
							newStatus !== null && newStatus !== undefined ? newStatus : false,
					},
				})
			);

			if (response && response.status === 'success') {
				message.success(
					response?.message ||
						`Vendor ${newStatus ? 'activated' : 'deactivated'} successfully`
				);
				setStatusModalVisible(false);
				setSelectedVendor(null);
				setNewStatus(null);
				// Refresh the vendor list
				fetchVendors();
			} else {
				const errorData = response?.error || response || {};
				const errorMessage =
					errorData?.message ||
					errorData?.details?.message ||
					'Failed to update vendor status';
				message.error(errorMessage);
			}
		} catch (error) {
			console.error('Error updating vendor status:', error);
			const errorMessage =
				error?.message || 'An unexpected error occurred. Please try again.';
			message.error(errorMessage);
		} finally {
			setUpdatingStatus(false);
		}
	};

	const handleStatusUpdateCancel = () => {
		setStatusModalVisible(false);
		setSelectedVendor(null);
		setNewStatus(null);
	};

	return (
		<Box>
			<Flex justifyContent="space-between" alignItems="center" mt={2} mb={2}>
				<Flex alignItems="center" gap="10px" flex="1">
					<Box flex="0 0 20%">
						<LtSearchBar
							placeholder="Search"
							enterButton="Search"
							allowClear
							onSearch={handleSearch}
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					</Box>
					<StyledFilterButton onClick={() => setFilterDrawerOpen(true)}>
						FILTERS
					</StyledFilterButton>
					<FilterDisplayBox>
						{hasDateRangeFilter() && (
							<FilterTag onClose={handleRemoveDateRangeFilter}>Date Range</FilterTag>
						)}
						{hasStatusFilter() && (
							<FilterTag onClose={handleRemoveStatusFilter}>Status</FilterTag>
						)}
						{hasVendorTypeFilter() && (
							<FilterTag onClose={handleRemoveVendorTypeFilter}>
								Vendor Type
							</FilterTag>
						)}
						{hasLegalStructureFilter() && (
							<FilterTag onClose={handleRemoveLegalStructureFilter}>
								Legal Structure
							</FilterTag>
						)}
						{hasBusinessUnitFilter() && (
							<FilterTag onClose={handleRemoveBusinessUnitFilter}>
								Business Unit
							</FilterTag>
						)}
						{hasCityFilter() && (
							<FilterTag onClose={handleRemoveCityFilter}>City</FilterTag>
						)}
						{activeFiltersCount > 0 && (
							<Tooltip title="Clear all filters">
								<CloseOutlined
									style={{
										color: '#ff4d4f',
										cursor: 'pointer',
										marginLeft: 'auto',
										marginRight: '4px',
									}}
									onClick={handleResetFilters}
								/>
							</Tooltip>
						)}
					</FilterDisplayBox>
				</Flex>
				<Flex gap="10px">
					<ButtonWrapper
						onClick={handleExport}
						icons={<ExportOutlined />}
						loading={exporting}
						disabled={exporting}
					>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />}>
						SYNC
					</ButtonWrapper>
					<ButtonWrapper onClick={handleCreate} icons={<PlusOutlined />}>
						CREATE
					</ButtonWrapper>
				</Flex>
			</Flex>

			<Box mt={3}>
				<Spin spinning={loading}>
					<AntdTable
						rowKey="id"
						columns={getTableColumns(navigate, handleStatusToggle)}
						dataSource={vendorsData}
						pagination={{
							current: pagination.current,
							pageSize: pagination.pageSize,
							total: pagination.total,
							showSizeChanger: true,
							pageSizeOptions: ['10', '20', '50', '100'],
							showTotal: (total) => `Total ${total} vendors`,
						}}
						onChange={handleTableChange}
					/>
				</Spin>
			</Box>

			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setFilterDrawerOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setFilterDrawerOpen(false)}
				open={filterDrawerOpen}
			>
				<Box>
					{/* Date Range Filter */}
					<Box mb="2rem">
						<Text mb="5px">Date Range</Text>
						<RangePicker
							style={{ width: '100%' }}
							value={dateRange}
							onChange={handleDateRangeChange}
							format="DD/MM/YYYY"
							placeholder={['Start Date', 'End Date']}
							allowClear
						/>
					</Box>

					{/* Status Filter */}
					<Box mb="2rem">
						<Text mb="5px">Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="is_active"
							value={filters.is_active}
							onChange={(val) => handleFilterChange('is_active', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={StatusOptions}
						/>
					</Box>

					{/* Vendor Type Filter */}
					<Box mb="2rem">
						<Text mb="5px">Vendor Type</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="vendor_type"
							value={filters.vendor_type}
							onChange={(val) => handleFilterChange('vendor_type', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={VendorTypeOptions}
						/>
					</Box>

					{/* Legal Structure Filter */}
					<Box mb="2rem">
						<Text mb="5px">Legal Structure</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="legal_structure"
							value={filters.legal_structure}
							onChange={(val) => handleFilterChange('legal_structure', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={LegalStructureOptions}
						/>
					</Box>

					{/* Business Unit Filter */}
					<Box mb="2rem">
						<Text mb="5px">Business Unit</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="business_unit_id"
							value={filters.business_unit_id}
							onChange={(val) => handleFilterChange('business_unit_id', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={filterOptions.businessUnits}
						/>
					</Box>

					{/* City Filter */}
					<Box mb="2rem">
						<Text mb="5px">City</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="city"
							value={filters.city}
							onChange={(val) => handleFilterChange('city', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={filterOptions.cities}
						/>
					</Box>

					{/* Reset Filters Button */}
					<Box>
						<ButtonWrapper onClick={handleResetFilters} style={{ width: '100%' }}>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>

			{/* Status Update Confirmation Modal */}
			<Modal
				title="Confirm Status Change"
				open={statusModalVisible}
				onOk={handleStatusUpdateConfirm}
				onCancel={handleStatusUpdateCancel}
				confirmLoading={updatingStatus}
				okText="Confirm"
				cancelText="Cancel"
			>
				<Text>
					Are you sure you want to {newStatus ? 'activate' : 'deactivate'} the vendor{' '}
					<strong>{selectedVendor?.vendor_name}</strong>?
				</Text>
			</Modal>
		</Box>
	);
}
